/**
 * Centralized logging utility for the application.
 *
 * This class provides standardized logging methods across the application
 * while ensuring that sensitive information is not exposed in production.
 */

import config from './config';

export class Logger {
  /**
   * Logs an informational message to the console in non-production environments.
   *
   * This method provides a centralized way to log informational messages during development
   * and testing. In production environments, info logging is suppressed to avoid
   * exposing sensitive information in the browser console.
   *
   * @param message - The primary informational message to log
   * @param optionalParams - Additional parameters or objects to include in the info log
   *
   * @example
   * ```TypeScript
   * const logger = new Logger();
   * logger.info('User logged in successfully', { userId: 123 });
   * ```
   */
  info(message: string, ...optionalParams: unknown[]): void {
    // if (config.app.isProduction) return;

    console.info(`ℹ️ [INFO]: ${message}`, ...optionalParams);
  }

  /**
   * Logs a warning message to the console in non-production environments.
   *
   * This method provides a centralized way to log warning messages during development
   * and testing. In production environments, warning logging is suppressed to avoid
   * exposing sensitive information in the browser console.
   *
   * @param message - The primary warning message to log
   * @param optionalParams - Additional parameters or objects to include in the warning log
   *
   * @example
   * ```TypeScript
   * const logger = new Logger();
   * logger.warn('Deprecated API usage', { endpoint: '/v1/old-endpoint' });
   * ```
   */
  warn(message: string, ...optionalParams: unknown[]): void {
    // if (config.app.isProduction) return;

    console.warn(`⚠️ [WARN]: ${message}`, ...optionalParams);
  }

  /**
   * Logs an error message to the console in non-production environments.
   *
   * This method provides a centralized way to log error messages during development
   * and testing. In production environments, error logging is suppressed to avoid
   * exposing sensitive information in the browser console.
   *
   * @param message - The primary error message to log
   * @param optionalParams - Additional parameters or objects to include in the error log
   *
   * @example
   * ```TypeScript
   * const logger = new Logger();
   * logger.error('Failed to fetch user data', { userId: 123, status: 404 });
   * ```
   */
  error(message: string, ...optionalParams: unknown[]): void {
    // if (config.app.isProduction) return;

    console.error(`❌ [ERROR]: ${message}`, ...optionalParams);
  }

  /**
   * Logs a debug message to the console in non-production environments.
   *
   * This method provides a centralized way to log debug messages during development
   * and testing. In production environments, debug logging is suppressed to avoid
   * exposing sensitive information in the browser console.
   *
   * @param message - The primary debug message to log
   * @param optionalParams - Additional parameters or objects to include in the debug log
   *
   * @example
   * ```TypeScript
   * const logger = new Logger();
   * logger.debug('User data fetched successfully', { userId: 123, data: userData });
   * ```
   */
  debug(message: string, ...optionalParams: unknown[]): void {
    if (config.isProduction) return;

    console.debug(`🐛 [DEBUG]: ${message}`, ...optionalParams);
  }

  /**
   * Logs a general message to the console.
   *
   * This method provides a standard console.log output with consistent formatting.
   *
   * @param message - The primary message to log
   * @param optionalParams - Additional parameters or objects to include in the log
   *
   * @example
   * ```TypeScript
   * const logger = new Logger();
   * logger.log('Application started', { version: '1.0.0' });
   * ```
   */
  log(message: string, ...optionalParams: unknown[]): void {
    // if (config.app.isProduction) return;

    console.log(`📝 [LOG]: ${message}`, ...optionalParams);
  }
}

// Export a singleton instance for convenience
export const logger = new Logger();
